import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Customer, Finding, ReportSummary, ScanResult } from "../types/customer";
import shadowScanLogo from "../assets/shadowscan-logo.png";
import { fetchApiJson } from "./api";

// Colors matching the app theme
const colors = {
  primary: [14, 165, 180] as [number, number, number], // Teal
  background: [17, 24, 39] as [number, number, number], // Dark navy
  cardBg: [25, 35, 52] as [number, number, number],
  text: [248, 250, 252] as [number, number, number],
  textMuted: [148, 163, 184] as [number, number, number],
  critical: [239, 68, 68] as [number, number, number],
  high: [234, 88, 88] as [number, number, number],
  medium: [245, 158, 11] as [number, number, number],
  low: [14, 165, 180] as [number, number, number],
  info: [100, 116, 139] as [number, number, number],
  success: [34, 197, 94] as [number, number, number],
};

const getSeverityColor = (severity: string): [number, number, number] => {
  switch (severity) {
    case "critical": return colors.critical;
    case "high": return colors.high;
    case "medium": return colors.medium;
    case "low": return colors.low;
    default: return colors.info;
  }
};

async function loadImageDataUrl(url: string): Promise<string> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to load image: ${url}`);
  const blob = await res.blob();
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Failed to read image"));
    reader.onload = () => resolve(String(reader.result || ""));
    reader.readAsDataURL(blob);
  });
}

async function getImageSize(dataUrl: string): Promise<{ width: number; height: number }> {
  return await new Promise((resolve, reject) => {
    const img = new Image();
    img.onerror = () => reject(new Error("Failed to decode image"));
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.src = dataUrl;
  });
}

function extractFirstCve(text: string): string | null {
  const match = String(text || "").match(/CVE-\d{4}-\d{4,7}/i);
  return match ? match[0].toUpperCase() : null;
}

function extractFirstExtractedValue(text: string): string | null {
  const m = String(text || "").match(/Extracted:\s*([^,\n]+)/i);
  if (!m || !m[1]) return null;
  return String(m[1]).trim().replace(/\.$/, "");
}

function toReferenceLink(reference: string | undefined): { display: string; url?: string } {
  const ref = String(reference || "").trim();
  if (!ref) return { display: "N/A" };

  const cve = extractFirstCve(ref);
  if (cve) return { display: cve, url: `https://nvd.nist.gov/vuln/detail/${cve}` };

  if (/^https?:\/\//i.test(ref)) return { display: ref, url: ref };

  return { display: ref };
}

async function fetchOpenAiSummary(input: string): Promise<string | null> {
  try {
    const json = await fetchApiJson<{ output_text?: string }>("/api/ai/summary", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ input }),
    });
    const text = typeof json.output_text === "string" ? json.output_text.trim() : "";
    if (!text) return null;
    return text;
  } catch {
    return null;
  }
}

function tryParseFirstJsonObject(text: string): unknown | null {
  const start = text.indexOf("{");
  const end = text.lastIndexOf("}");
  if (start === -1 || end === -1 || end <= start) return null;
  try {
    return JSON.parse(text.slice(start, end + 1));
  } catch {
    return null;
  }
}

async function fetchOpenAiSections(prompt: string): Promise<{ remediation?: string; impact?: string } | null> {
  const raw = await fetchOpenAiSummary(prompt);
  if (!raw) return null;
  const parsed = tryParseFirstJsonObject(raw) as { remediation?: unknown; impact?: unknown } | null;
  if (!parsed || typeof parsed !== "object") return null;
  const remediation = typeof parsed.remediation === "string" ? parsed.remediation.trim() : undefined;
  const impact = typeof parsed.impact === "string" ? parsed.impact.trim() : undefined;
  if (!remediation && !impact) return null;
  return { remediation, impact };
}

function uniqueTop(values: string[], max: number): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const v of values) {
    const t = String(v || "").trim();
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
    if (out.length >= max) break;
  }
  return out;
}

function normalizeBodyText(text: string): string {
  const rawLines = String(text || "").split(/\r?\n/);
  while (rawLines.length > 0 && rawLines[rawLines.length - 1].trim() === "") rawLines.pop();
  const out: string[] = [];
  let lastWasEmpty = false;
  for (const line of rawLines) {
    const isEmpty = line.trim() === "";
    if (isEmpty) {
      if (lastWasEmpty) continue;
      out.push("");
      lastWasEmpty = true;
      continue;
    }
    out.push(line);
    lastWasEmpty = false;
  }
  return out.join("\n");
}

function wrapPreserveNewlines(doc: jsPDF, text: string, width: number): string[] {
  const breakLongToken = (token: string): string[] => {
    const out: string[] = [];
    let rest = String(token || "");
    const delims = ["/", "?", "&", "-", "_", ".", ":", "=", "#"];

    while (rest) {
      if (doc.getTextWidth(rest) <= width) {
        out.push(rest);
        break;
      }

      let lo = 1;
      let hi = rest.length;
      let best = 1;
      while (lo <= hi) {
        const mid = Math.floor((lo + hi) / 2);
        const seg = rest.slice(0, mid);
        if (doc.getTextWidth(seg) <= width) {
          best = mid;
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }

      let cut = best;
      const prefix = rest.slice(0, best);
      let prefer = -1;
      for (const d of delims) {
        const idx = prefix.lastIndexOf(d);
        if (idx > 0 && idx + 1 < best) prefer = Math.max(prefer, idx + 1);
      }
      if (prefer > 0 && prefer >= Math.floor(best * 0.6)) cut = prefer;

      out.push(rest.slice(0, cut));
      rest = rest.slice(cut);
    }

    return out;
  };

  const wrapLine = (line: string): string[] => {
    const raw = String(line || "");
    if (!raw.trim()) return [""];

    const m = raw.match(/^(\s*-\s+)/);
    const bulletPrefix = m?.[1] ? m[1] : "";
    const body = bulletPrefix ? raw.slice(bulletPrefix.length) : raw;
    const firstPrefix = bulletPrefix || "";
    const subsequentPrefix = bulletPrefix ? "  " : "";

    type Prefix = { value: string; available: number };
    const prefixFor = (v: string): Prefix => ({
      value: v,
      available: Math.max(5, width - doc.getTextWidth(v)),
    });

    const out: string[] = [];
    let current = "";
    let prefix = prefixFor(firstPrefix);

    const flush = () => {
      if (!current) return;
      out.push(`${prefix.value}${current}`);
      current = "";
      prefix = prefixFor(subsequentPrefix);
    };

    const pushTokenChunks = (chunks: string[]) => {
      if (chunks.length === 0) return;
      for (let i = 0; i < chunks.length; i += 1) {
        const chunk = chunks[i];
        if (!chunk) continue;
        if (i < chunks.length - 1) {
          out.push(`${prefix.value}${chunk}`);
          prefix = prefixFor(subsequentPrefix);
          continue;
        }
        current = chunk;
      }
    };

    const words = body.split(/\s+/).filter(Boolean);
    for (const word of words) {
      const candidate = current ? `${current} ${word}` : word;
      if (doc.getTextWidth(candidate) <= prefix.available) {
        current = candidate;
        continue;
      }

      if (current) flush();

      if (doc.getTextWidth(word) <= prefix.available) {
        current = word;
        continue;
      }

      pushTokenChunks(breakLongToken(word));
      flush();
    }

    flush();
    return out.length > 0 ? out : [raw];
  };

  const lines: string[] = [];
  const normalized = normalizeBodyText(text);
  for (const line of normalized.split("\n")) {
    if (line.trim() === "") {
      lines.push("");
      continue;
    }
    for (const w of wrapLine(line)) lines.push(w);
  }
  while (lines.length > 0 && String(lines[lines.length - 1]).trim() === "") lines.pop();
  return lines;
}

function extractPluginSlug(title: string): string | null {
  const m1 = String(title || "").match(/^Plugin detected:\s*([^\s(]+)/i);
  if (m1) return m1[1];
  const m2 = String(title || "").match(/^Plugin\s*\(([^)]+)\):/i);
  if (m2) return m2[1];
  const m0 = String(title || "").match(/^Plugin observed:\s*(.+)$/i);
  if (m0) return m0[1].trim();
  // Nuclei plugin-detect templates: e.g. "Wordpress Plugin Detection (duracelltomi-google-tag-manager)"
  if (/(plugin\s+detection|plugin[-\s]?detect|wordpress[-\s]?plugin)/i.test(String(title || ""))) {
    const m3 = String(title || "").match(/\(([^)]+)\)\s*$/);
    if (m3) return m3[1].trim();
  }
  return null;
}

function extractWpOrgPluginSlug(reference?: string): string | null {
  const ref = String(reference || "");
  const m = ref.match(/https?:\/\/wordpress\.org\/plugins\/([^/]+)\/?/i);
  return m && m[1] ? m[1] : null;
}

function extractThemeSlug(title: string): string | null {
  const m1 = String(title || "").match(/^Theme detected:\s*([^\s(]+)/i);
  if (m1) return m1[1];
  const m2 = String(title || "").match(/^Theme\s*\(([^)]+)\):/i);
  if (m2) return m2[1];
  return null;
}

function extractDetectedVersionFromTitle(title: string): string | null {
  const t = String(title || "");
  const m = t.match(/:\s*[^\s]+\s+([0-9]+(?:\.[0-9]+){0,3}[a-z0-9.\-+]*)(\s|\(|$)/i);
  return m && m[1] ? m[1] : null;
}

function isUserEnumFinding(finding: Finding): boolean {
  const scanner = String(finding.scanner || "").toLowerCase();
  const title = String(finding.title || "");
  if (scanner === "wpscan" && /^user enumerated:/i.test(title)) return true;
  if (scanner === "sitecheck" && /(user[-\s]?enum|user\s+enumeration)/i.test(title)) return true;
  if (scanner === "nuclei" && /(user[-\s]?enum|user\\s+enumeration)/i.test(title)) return true;
  return false;
}

function extractUserEnumUsers(finding: Finding): string[] {
  const scanner = String(finding.scanner || "").toLowerCase();
  const title = String(finding.title || "");
  const description = String(finding.description || "");

  if (scanner === "wpscan" && /^user enumerated:/i.test(title)) {
    return [title.replace(/^User enumerated:\s*/i, "").trim()].filter(Boolean);
  }

  if (scanner === "nuclei") {
    const m = title.match(/\(([^)]+)\)\s*$/);
    if (m && m[1]) return [m[1].trim()].filter(Boolean);
  }

  const extractedMatch = description.match(/Extracted:\s*([^.\n]+)/i);
  if (extractedMatch && extractedMatch[1]) {
    return extractedMatch[1]
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }

  return [];
}

function renderSectionHeader(doc: jsPDF, title: string, x: number, y: number) {
  doc.setFontSize(14);
  doc.setTextColor(...colors.text);
  doc.setFont("helvetica", "bold");
  doc.text(title, x, y);
}

function renderCardText(
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  width: number,
  maxLines: number,
): { lines: string[]; height: number } {
  doc.setFontSize(10);
  doc.setTextColor(...colors.text);
  doc.setFont("helvetica", "normal");
  const lines = doc.splitTextToSize(text, width);
  const slice = lines.slice(0, maxLines);
  doc.text(slice, x, y);
  return { lines: slice, height: slice.length * 5 };
}

export async function generateSecurityReport(
  customer: Customer,
  rawFindings: Finding[],
  summary: ReportSummary,
  options?: {
    scannersUsed?: string[];
    scanTimestamp?: string;
    scanResults?: ScanResult[];
    output?: "download" | "blob";
    filename?: string;
  }
): Promise<void | Blob> {
  const enhanceFindings = (items: Finding[]) => {
    const looksLikeVersion = (v: string) => /^\d+(\.\d+){0,3}([a-z0-9.\-+]+)?$/i.test(String(v || "").trim());
    return items.map((f) => {
      const scanner = String(f.scanner || "").toLowerCase();
      if (scanner !== "nuclei") return f;
      const extracted = extractFirstExtractedValue(f.description);
      if (!extracted || !looksLikeVersion(extracted)) return f;
      const title = String(f.title || "");
      if (title.toLowerCase().includes(extracted.toLowerCase())) return f;
      const isWpDetect = /^word\s*press\s*detect/i.test(title);
      const isWpOrgPlugin = /https?:\/\/wordpress\.org\/plugins\//i.test(String(f.reference || ""));
      const isPluginDetection = /plugin/i.test(title) && /detection/i.test(title);
      if (!isWpDetect && !isWpOrgPlugin && !isPluginDetection) return f;
      return { ...f, title: `${title} (${extracted})` };
    });
  };

  const findings = enhanceFindings(rawFindings);
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 12;
  let yPosition = margin;
  let lastFooterPage = -1;

  const paintStandardPage = () => {
    doc.setFillColor(...colors.background);
    doc.rect(0, 0, pageWidth, pageHeight, "F");
    doc.setFillColor(...colors.primary);
    doc.rect(0, 0, pageWidth, 8, "F");
  };

  // Helper to add page if needed
  const checkPageBreak = (requiredHeight: number) => {
    if (yPosition + requiredHeight > pageHeight - margin) {
      addFooter();
      doc.addPage();
      paintStandardPage();
      yPosition = 25;
    }
  };

  // Footer function
  const addFooter = () => {
    const page = doc.getNumberOfPages();
    if (page === lastFooterPage) return;
    lastFooterPage = page;
    const footerY = pageHeight - 8;
    doc.setFontSize(8);
    doc.setTextColor(...colors.textMuted);
    const pageLabel = `ShadowScan Security Report - Confidential - Page ${doc.getNumberOfPages()}`;
    doc.text(pageLabel, margin, footerY, { align: "left" });

    const website = "https://shadowscan.com.au";
    const email = "support@shadowscan.com.au";
    const rightText = `${website} | ${email}`;
    doc.text(rightText, pageWidth - margin, footerY, { align: "right" });

    // Clickable links on the right side
    const fullWidth = doc.getTextWidth(rightText);
    const startX = pageWidth - margin - fullWidth;
    const sep = " | ";
    const websiteW = doc.getTextWidth(website);
    const sepW = doc.getTextWidth(sep);
    const emailW = doc.getTextWidth(email);
    doc.link(startX, footerY - 3.2, websiteW, 4.2, { url: website });
    doc.link(startX + websiteW + sepW, footerY - 3.2, emailW, 4.2, { url: `mailto:${email}` });
  };

  // === COVER PAGE ===
  // Dark background
  doc.setFillColor(...colors.background);
  doc.rect(0, 0, pageWidth, pageHeight, "F");

  // Company branding header bar
  doc.setFillColor(...colors.primary);
  doc.rect(0, 0, pageWidth, 8, "F");

  // Logo
  try {
    const logoDataUrl = await loadImageDataUrl(shadowScanLogo);
    const { width, height } = await getImageSize(logoDataUrl);
    const targetW = 28;
    const targetH = Math.max(10, (targetW * height) / Math.max(1, width));
    // Top-left, below the header bar
    doc.addImage(logoDataUrl, "PNG", margin, 14, targetW, targetH);
  } catch {
    // ignore logo load failures
  }

  // Title section
  doc.setFontSize(32);
  doc.setTextColor(...colors.text);
  doc.setFont("helvetica", "bold");
  doc.text("SECURITY", pageWidth / 2, 60, { align: "center" });
  doc.text("ASSESSMENT", pageWidth / 2, 75, { align: "center" });
  doc.text("REPORT", pageWidth / 2, 90, { align: "center" });

  // Decorative line
  doc.setDrawColor(...colors.primary);
  doc.setLineWidth(2);
  doc.line(margin + 30, 100, pageWidth - margin - 30, 100);

  // Client info box
  doc.setFillColor(...colors.cardBg);
  doc.roundedRect(margin, 115, pageWidth - 2 * margin, 50, 3, 3, "F");
  
  doc.setFontSize(12);
  doc.setTextColor(...colors.textMuted);
  doc.setFont("helvetica", "normal");
  doc.text("PREPARED FOR", margin + 10, 130);
  
  doc.setFontSize(18);
  doc.setTextColor(...colors.text);
  doc.setFont("helvetica", "bold");
  doc.text(customer.name, margin + 10, 145);
  
  doc.setFontSize(11);
  doc.setTextColor(...colors.primary);
  doc.setFont("helvetica", "normal");
  doc.text(customer.url, margin + 10, 157);

  // Report metadata
  doc.setFillColor(...colors.cardBg);
  doc.roundedRect(margin, 175, pageWidth - 2 * margin, 35, 3, 3, "F");
  
  const reportDate = new Date().toLocaleDateString("en-AU", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  
  doc.setFontSize(10);
  doc.setTextColor(...colors.textMuted);
  doc.text("REPORT DATE", margin + 10, 188);
  doc.text("PREPARED BY", pageWidth / 2 + 10, 188);
  
  doc.setFontSize(12);
  doc.setTextColor(...colors.text);
  doc.text(reportDate, margin + 10, 200);
  doc.text("ShadowScan Security", pageWidth / 2 + 10, 200);

  // Threat summary box
  doc.setFillColor(...colors.cardBg);
  doc.roundedRect(margin, 220, pageWidth - 2 * margin, 50, 3, 3, "F");
  
  doc.setFontSize(12);
  doc.setTextColor(...colors.textMuted);
  doc.text("THREAT SUMMARY", margin + 10, 235);
  
  // Summary stats
  const statsWidth = (pageWidth - 2 * margin - 20) / 5;
  const statsY = 255;
  const statLabels = ["CRITICAL", "HIGH", "MEDIUM", "LOW", "INFO"];
  const statValues = [summary.critical, summary.high, summary.medium, summary.low, summary.info];
  const statColors = [colors.critical, colors.high, colors.medium, colors.low, colors.info];
  
  statLabels.forEach((label, i) => {
    const x = margin + 10 + i * statsWidth + statsWidth / 2;
    doc.setFontSize(20);
    doc.setTextColor(...statColors[i]);
    doc.setFont("helvetica", "bold");
    doc.text(statValues[i].toString(), x, statsY, { align: "center" });
    doc.setFontSize(7);
    doc.setTextColor(...colors.textMuted);
    doc.setFont("helvetica", "normal");
    doc.text(label, x, statsY + 8, { align: "center" });
  });

  // Confidentiality notice
  doc.setFontSize(8);
  doc.setTextColor(...colors.textMuted);
  doc.text(
    "CONFIDENTIAL - This document contains sensitive security information.",
    pageWidth / 2,
    pageHeight - 30,
    { align: "center" }
  );
  doc.text(
    "Unauthorized distribution is prohibited.",
    pageWidth / 2,
    pageHeight - 23,
    { align: "center" }
  );

  addFooter();

  // === EXECUTIVE SUMMARY PAGE ===
  doc.addPage();
  doc.setFillColor(...colors.background);
  doc.rect(0, 0, pageWidth, pageHeight, "F");
  doc.setFillColor(...colors.primary);
  doc.rect(0, 0, pageWidth, 8, "F");

  yPosition = 25;
  
  doc.setFontSize(22);
  doc.setTextColor(...colors.text);
  doc.setFont("helvetica", "bold");
  doc.text("Executive Summary", margin, yPosition);
  yPosition += 15;

  // Divider
  doc.setDrawColor(...colors.primary);
  doc.setLineWidth(1);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 15;

  // Overview
  doc.setFontSize(11);
  doc.setTextColor(...colors.text);
  doc.setFont("helvetica", "normal");
  
  const scannerLabel = (scanner: string) => {
    const s = scanner.toLowerCase();
    if (s === "sitecheck") return "Sitecheck";
    if (s === "httpx") return "httpx";
    if (s === "wpscan") return "WPScan";
    if (s === "nuclei") return "Nuclei";
    return scanner;
  };
  const scannersUsedText =
    options?.scannersUsed && options.scannersUsed.length > 0
      ? options.scannersUsed.join(", ")
      : Array.from(new Set(findings.map((f) => scannerLabel(f.scanner)))).join(", ") || "industry-standard tools";
  const overviewText = `This security assessment was conducted on ${customer.url} to identify potential vulnerabilities and security weaknesses. The assessment utilized ${scannersUsedText} to perform vulnerability scanning and security checks.`;
  
  const overviewLines = doc.splitTextToSize(overviewText, pageWidth - 2 * margin);
  doc.text(overviewLines, margin, yPosition);
  yPosition += overviewLines.length * 6 + 10;

  // OpenAI Summary (placed under Executive Summary)
  const topFindingTitles = findings
    .filter((f) => f.severity === "critical" || f.severity === "high" || f.severity === "medium")
    .slice(0, 6)
    .map((f) => `- ${f.title}`);
  const aiPrompt = [
    "Write a concise executive summary for a WordPress security report.",
    `Target: ${customer.url}`,
    `Findings: total=${summary.totalFindings} critical=${summary.critical} high=${summary.high} medium=${summary.medium} low=${summary.low} info=${summary.info}`,
    topFindingTitles.length ? "Top findings:\n" + topFindingTitles.join("\n") : "Top findings: none provided",
    "",
    "Requirements:",
    "- 5-8 bullet points, max 140 words total.",
    "- Summarise key risks in plain English.",
    "- Explain why ShadowScan is a good choice for ongoing WordPress security services (monitoring, retesting, remediation guidance).",
    "- Keep it professional; no hype.",
  ].join("\n");
  const aiSummary = await fetchOpenAiSummary(aiPrompt);
  if (aiSummary) {
    checkPageBreak(70);
    doc.setFontSize(14);
    doc.setTextColor(...colors.text);
    doc.setFont("helvetica", "bold");
    doc.text("Scan Summary", margin, yPosition);
    yPosition += 10;

    doc.setFillColor(...colors.cardBg);
    doc.roundedRect(margin, yPosition, pageWidth - 2 * margin, 48, 3, 3, "F");
    doc.setFontSize(10);
    doc.setTextColor(...colors.text);
    doc.setFont("helvetica", "normal");
    const lines = doc.splitTextToSize(aiSummary, pageWidth - 2 * margin - 16);
    doc.text(lines.slice(0, 8), margin + 8, yPosition + 14);
    yPosition += 58;
  }

  // Risk Score Section
  doc.setFillColor(...colors.cardBg);
  doc.roundedRect(margin, yPosition, pageWidth - 2 * margin, 45, 3, 3, "F");
  
  const overallRiskScore = Math.min(
    100,
    summary.critical * 30 + summary.high * 22 + summary.medium * 8 + summary.low * 3 + summary.info * 1,
  );
  // Never downplay severity: any critical/high finding should elevate the overall rating.
  const overallRiskLevel =
    summary.critical > 0 || summary.high > 0 ? "HIGH" : summary.medium > 0 ? "MEDIUM" : "LOW";
  const overallRiskColor = overallRiskLevel === "HIGH" ? colors.critical : overallRiskLevel === "MEDIUM" ? colors.medium : colors.success;
  
  doc.setFontSize(10);
  doc.setTextColor(...colors.textMuted);
  doc.text("OVERALL RISK SCORE", margin + 15, yPosition + 15);
  
  doc.setFontSize(28);
  doc.setTextColor(...overallRiskColor);
  doc.setFont("helvetica", "bold");
  doc.text(`${overallRiskScore}`, margin + 15, yPosition + 35);
  doc.setFontSize(14);
  doc.text(overallRiskLevel, margin + 45, yPosition + 35);
  
  // Total findings
  doc.setFontSize(10);
  doc.setTextColor(...colors.textMuted);
  doc.setFont("helvetica", "normal");
  doc.text("TOTAL FINDINGS", pageWidth - margin - 60, yPosition + 15);
  doc.setFontSize(28);
  doc.setTextColor(...colors.text);
  doc.setFont("helvetica", "bold");
  doc.text(summary.totalFindings.toString(), pageWidth - margin - 60, yPosition + 35);
  
  yPosition += 60;

  // Key Findings Summary
  doc.setFontSize(14);
  doc.setTextColor(...colors.text);
  doc.setFont("helvetica", "bold");
  doc.text("Key Findings", margin, yPosition);
  yPosition += 10;

  // Key findings list: prefer critical/high; fill with medium so we show ~3 items when possible.
  const keySeverityRank = (s: Finding["severity"]) =>
    s === "critical" ? 4 : s === "high" ? 3 : s === "medium" ? 2 : s === "low" ? 1 : 0;
  const keyFindings = findings
    .filter((f) => f.severity === "critical" || f.severity === "high" || f.severity === "medium")
    .slice()
    .sort(
      (a, b) =>
        keySeverityRank(b.severity) - keySeverityRank(a.severity) ||
        String(a.title || "").localeCompare(String(b.title || "")),
    );
  const keyUnique: Finding[] = [];
  const seenKey = new Set<string>();
  for (const f of keyFindings) {
    const k = `${String(f.title || "").trim().toLowerCase()}|${String(f.scanner || "").trim().toLowerCase()}`;
    if (!k || seenKey.has(k)) continue;
    seenKey.add(k);
    keyUnique.push(f);
    if (keyUnique.length >= 3) break;
  }
  if (keyUnique.length > 0) {
    keyUnique.forEach((finding) => {
      checkPageBreak(20);
      doc.setFillColor(...getSeverityColor(finding.severity));
      doc.roundedRect(margin, yPosition, 4, 12, 1, 1, "F");
      
      doc.setFontSize(10);
      doc.setTextColor(...colors.text);
      doc.setFont("helvetica", "bold");
      doc.text(finding.title, margin + 10, yPosition + 8);
      yPosition += 18;
    });
  } else {
    doc.setFontSize(10);
    doc.setTextColor(...colors.success);
    doc.text("No critical/high/medium severity findings detected.", margin, yPosition + 8);
    yPosition += 18;
  }

  addFooter();

  // === ASSESSMENT OVERVIEW PAGE ===
  doc.addPage();
  doc.setFillColor(...colors.background);
  doc.rect(0, 0, pageWidth, pageHeight, "F");
  doc.setFillColor(...colors.primary);
  doc.rect(0, 0, pageWidth, 8, "F");
  yPosition = 25;

  doc.setFontSize(22);
  doc.setTextColor(...colors.text);
  doc.setFont("helvetica", "bold");
  doc.text("Assessment Overview", margin, yPosition);
  yPosition += 12;

  doc.setDrawColor(...colors.primary);
  doc.setLineWidth(1);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 12;

  const hasWpScan = findings.some((f) => String(f.scanner || "").toLowerCase() === "wpscan");
  const hasSitecheck = findings.some((f) => String(f.scanner || "").toLowerCase() === "sitecheck");
  const hasHttpx = findings.some((f) => String(f.scanner || "").toLowerCase() === "httpx");
  const wpVersionFinding =
    findings.find((f) => String(f.title || "").toLowerCase().startsWith("wordpress version detected:")) ||
    findings.find((f) => String(f.title || "").toLowerCase().includes("wp_version="));
  const nucleiWpDetect = findings
    .filter((f) => String(f.scanner || "").toLowerCase() === "nuclei")
    .find((f) => /^word\s*press\s*detect/i.test(String(f.title || "")) && extractFirstExtractedValue(f.description));
  const nucleiWpVersion = nucleiWpDetect ? extractFirstExtractedValue(nucleiWpDetect.description) : null;
  const coreVulns = findings.filter((f) => String(f.title || "").toLowerCase().startsWith("core:"));
  const pluginRelated = findings.filter((f) => {
    const t = String(f.title || "").toLowerCase();
    return (
      t.startsWith("plugin detected:") ||
      t.startsWith("plugin (") ||
      t.includes("plugin detection") ||
      t.includes("plugin-detect") ||
      t.includes("wordpress plugin") ||
      /https?:\/\/wordpress\.org\/plugins\//i.test(String(f.reference || ""))
    );
  });
  const themeRelated = findings.filter((f) => {
    const t = String(f.title || "").toLowerCase();
    return t.startsWith("theme detected:") || t.startsWith("theme (");
  });
  const userEnumFindings = findings.filter((f) => isUserEnumFinding(f));
  const sitecheckFindings = findings.filter((f) => String(f.scanner || "").toLowerCase() === "sitecheck");
  const httpxFindings = findings.filter((f) => String(f.scanner || "").toLowerCase() === "httpx");

  const openAiSectionsPrompt = [
    "Return ONLY valid JSON with keys: remediation, impact.",
    "You are writing for a WordPress site owner.",
    `Target: ${customer.url}`,
    `Risk score: ${overallRiskScore}/100 (${overallRiskLevel})`,
    `Findings: total=${summary.totalFindings} critical=${summary.critical} high=${summary.high} medium=${summary.medium} low=${summary.low} info=${summary.info}`,
    "In remediation: 5-8 bullet points (use leading hyphen), focused on immediate actions and why ShadowScan is a good ongoing partner (monitoring, patch management guidance, retesting).",
    "In impact: 4-6 bullet points (use leading hyphen) describing business impact if left unresolved (compromise, downtime, SEO spam, legal/compliance, reputation).",
  ].join("\n");
  const aiSections = await fetchOpenAiSections(openAiSectionsPrompt);

  const sectionWidth = pageWidth - 2 * margin;
  const cardWidth = sectionWidth;

  const renderSectionCard = (title: string, body: string, maxLines = 10) => {
    // Auto-size cards to content so we don't leave large empty blocks.
    // Keep a max line cap to avoid overly tall sections.
    const headerH = 8;
    const gapAfter = 12;

    // IMPORTANT: `renderSectionHeader()` mutates the font state (bold/size). Compute wrapping and
    // render body text using an explicitly-set body font to avoid overflow.
    doc.setFontSize(10);
    doc.setTextColor(...colors.text);
    doc.setFont("helvetica", "normal");
    const wrapped = wrapPreserveNewlines(doc, body, cardWidth - 16);
    const lines = wrapped.slice(0, Math.max(1, maxLines));
    const lineH = 5;
    const padTop = 12;
    const padBottom = 10;
    const cardHeight = Math.max(28, padTop + lines.length * lineH + padBottom);

    checkPageBreak(headerH + cardHeight + gapAfter);

    renderSectionHeader(doc, title, margin, yPosition);
    yPosition += headerH;

    // Restore body font after header.
    doc.setFontSize(10);
    doc.setTextColor(...colors.text);
    doc.setFont("helvetica", "normal");

    doc.setFillColor(...colors.cardBg);
    doc.roundedRect(margin, yPosition, cardWidth, cardHeight, 3, 3, "F");
    doc.text(lines, margin + 8, yPosition + padTop);
    yPosition += cardHeight + gapAfter;
  };

  // Scan Completeness
  const scanResults = Array.isArray(options?.scanResults) ? options.scanResults : [];
  if (scanResults.length > 0) {
    const labelFor = (scanner: string) => {
      const s = String(scanner || "").toLowerCase();
      if (s === "sitecheck") return "Site Checks";
      if (s === "httpx") return "httpx";
      if (s === "wpscan") return "WPScan";
      if (s === "nuclei") return "Nuclei";
      return scanner;
    };
    const statusBits = scanResults
      .slice()
      .sort((a, b) => String(a.scanner).localeCompare(String(b.scanner)))
      .map((r) => {
        const s = String(r.scanner || "").toLowerCase();
        const status = String(r.status || "").toUpperCase();
        const note = s === "wpscan" && String(r.status || "").toLowerCase() !== "success" ? " (incomplete)" : "";
        return `- ${labelFor(r.scanner)}: ${status}${note}`;
      });
    renderSectionCard("Scan Completeness", statusBits.join("\n"), 8);
  }

  // Hosting & Infrastructure Overview
  const infraBits: string[] = [];
  if (hasSitecheck) {
    for (const f of sitecheckFindings) infraBits.push(f.title);
  }
  if (hasHttpx) {
    for (const f of httpxFindings) infraBits.push(f.title);
  }
  const infraBody =
    infraBits.length > 0
      ? uniqueTop(infraBits, 8).map((t) => `- ${t}`).join("\n")
      : "- Site-level checks were not available for this scan.";
  renderSectionCard("Hosting & Infrastructure Overview", infraBody, 10);

  // WordPress Core Security Status
  const coreBits: string[] = [];
  if (wpVersionFinding) coreBits.push(`- ${wpVersionFinding.title}`);
  if (!wpVersionFinding && nucleiWpVersion) coreBits.push(`- WordPress version detected (via Nuclei): ${nucleiWpVersion}`);
  if (coreVulns.length) coreBits.push(`- Core vulnerabilities reported: ${coreVulns.length}`);
  if (!hasWpScan) coreBits.push("- WPScan results were not available for this scan.");
  const coreBody = coreBits.length ? coreBits.join("\n") : "- No WordPress core status could be derived from scan data.";
  renderSectionCard("WordPress Core Security Status", coreBody, 8);

  // Theme Security Findings
  const themeSlugs = uniqueTop(themeRelated.map((f) => extractThemeSlug(f.title) || ""), 8);
  const themeBody =
    themeRelated.length > 0
      ? [
          `- Theme-related findings: ${themeRelated.length}`,
          themeSlugs.length ? `- Themes observed: ${themeSlugs.join(", ")}` : null,
          (() => {
            const themeInventory = uniqueTop(
              themeRelated
                .filter((f) => /^theme detected:/i.test(String(f.title || "")))
                .map((f) => {
                  const slug = extractThemeSlug(f.title) || "unknown";
                  const ver = extractDetectedVersionFromTitle(f.title);
                  const suffix = ver ? ` ${ver}` : "";
                  const outdated = /\(outdated\)/i.test(String(f.title || ""));
                  return `${slug}${suffix}${outdated ? " (outdated)" : ""}`;
                }),
              8,
            );
            return themeInventory.length ? `- Theme inventory: ${themeInventory.join(", ")}` : null;
          })(),
        ]
          .filter(Boolean)
          .join("\n")
      : "- No theme findings were reported.";
  renderSectionCard("Theme Security Findings", themeBody, 8);

  // Plugin Security Findings
  const pluginSlugs = uniqueTop(
    pluginRelated.map((f) => extractPluginSlug(f.title) || extractWpOrgPluginSlug(f.reference) || ""),
    30,
  );
  const pluginBody =
    pluginRelated.length > 0
      ? [
          `- Plugin-related findings: ${pluginRelated.length}`,
          pluginSlugs.length ? `- Plugins observed: ${pluginSlugs.join(", ")}` : null,
          (() => {
            const pluginInventory = uniqueTop(
              pluginRelated
                .filter((f) => /^(plugin detected:|plugin observed:)/i.test(String(f.title || "")))
                .map((f) => {
                  const slug = extractPluginSlug(f.title) || "unknown";
                  const ver = extractDetectedVersionFromTitle(f.title);
                  const suffix = ver ? ` ${ver}` : "";
                  const outdated = /\(outdated\)/i.test(String(f.title || ""));
                  return `${slug}${suffix}${outdated ? " (outdated)" : ""}`;
                }),
              30,
            );
            return pluginInventory.length ? `- Plugin inventory: ${pluginInventory.join(", ")}` : null;
          })(),
        ]
          .filter(Boolean)
          .join("\n")
      : "- No plugin findings were reported.";
  renderSectionCard("Plugin Security Findings", pluginBody, 9);

  // Exposed Endpoints (WPScan interesting findings)
  const endpointFindings = findings.filter((f) => {
    if (String(f.scanner || "").toLowerCase() !== "wpscan") return false;
    const t = String(f.title || "").toLowerCase();
    return (
      t.includes("xml-rpc") ||
      t.includes("robots.txt") ||
      t.includes("must use plugins") ||
      t.includes("wp-cron")
    );
  });
  const endpointBody =
    endpointFindings.length > 0
      ? uniqueTop(endpointFindings.map((f) => f.title), 8).map((t) => `- ${t}`).join("\n")
      : "- No exposed endpoints were reported.";
  renderSectionCard("Exposed Endpoints", endpointBody, 10);

  // Scan Statistics (WPScan)
  const wpscanStats = findings.find(
    (f) => String(f.scanner || "").toLowerCase() === "wpscan" && /^wpscan scan statistics/i.test(String(f.title || "")),
  );
  if (wpscanStats?.description) {
    renderSectionCard("Scan Statistics", wpscanStats.description, 10);
  }

  // User Enumeration Exposure
  const enumeratedUsers = uniqueTop(userEnumFindings.flatMap(extractUserEnumUsers), 500);
  const userRisk =
    enumeratedUsers.some((u) => u.toLowerCase() === "admin") ? "High" : enumeratedUsers.length > 0 ? "Medium-High" : "Low";
  const userBody =
    enumeratedUsers.length > 0
      ? [
          `Discovered user${enumeratedUsers.length > 1 ? "s" : ""}: ${enumeratedUsers.length}`,
          `Risk Level: ${userRisk}`,
          "",
          "Impact:",
          "- Enables targeted brute-force attacks",
          "- Enables phishing and social engineering",
          "- Assists password-spray attacks",
        ].join("\n")
      : "- No users were enumerated in the scan output (or enumeration was blocked).";
  renderSectionCard("User Enumeration Exposure", userBody, 10);

  // Overall Risk Rating
  renderSectionCard("Overall Risk Rating", `- ${overallRiskScore}/100 (${overallRiskLevel})`, 3);

  // Immediate Remediation Requirements (OpenAI-assisted if available)
  const remediationBody =
    aiSections?.remediation ||
    [
      "- Patch WordPress core, plugins, and themes (prioritise critical/high findings).",
      "- Reduce exposure: lock down XML-RPC, restrict sensitive endpoints, and enforce least-privilege access.",
      "- Implement monitoring, scheduled rescans, and post-fix retesting.",
    ].join("\n");
  renderSectionCard("Immediate Remediation Requirements", remediationBody, 10);

  // Business Impact if Left Unresolved (OpenAI-assisted if available)
  const impactBody =
    aiSections?.impact ||
    [
      "- Increased likelihood of compromise leading to defacement, malware, or SEO spam.",
      "- Potential downtime and incident response costs.",
      "- Reputational damage and possible legal/compliance exposure depending on stored data.",
    ].join("\n");
  renderSectionCard("Business Impact if Left Unresolved", impactBody, 9);

  addFooter();

  // === DETAILED FINDINGS PAGE ===
  doc.addPage();
  doc.setFillColor(...colors.background);
  doc.rect(0, 0, pageWidth, pageHeight, "F");
  doc.setFillColor(...colors.primary);
  doc.rect(0, 0, pageWidth, 8, "F");

  yPosition = 25;
  
  doc.setFontSize(22);
  doc.setTextColor(...colors.text);
  doc.setFont("helvetica", "bold");
  doc.text("Detailed Findings", margin, yPosition);
  yPosition += 15;

  doc.setDrawColor(...colors.primary);
  doc.setLineWidth(1);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 15;

  // Findings table
  const severityRank = (severity: string) => {
    const s = String(severity || "").toLowerCase();
    if (s === "info") return 0;
    if (s === "low") return 1;
    if (s === "medium") return 2;
    if (s === "high") return 3;
    if (s === "critical") return 4;
    return 5;
  };
  const sortedFindings = findings
    .slice()
    .sort(
      (a, b) =>
        severityRank(a.severity) - severityRank(b.severity) ||
        String(a.scanner || "").localeCompare(String(b.scanner || "")) ||
        String(a.title || "").localeCompare(String(b.title || "")),
    );

  const tableData = sortedFindings.map((finding) => {
    const ref = toReferenceLink(finding.reference);
    return [
      finding.severity.toUpperCase(),
      finding.title,
      scannerLabel(finding.scanner).toUpperCase().replace(" ", ""),
      ref.display,
    ];
  });

  autoTable(doc, {
    startY: yPosition,
    head: [["SEVERITY", "FINDING", "SCANNER", "REFERENCE"]],
    body: tableData,
    theme: "plain",
    willDrawPage: () => {
      paintStandardPage();
    },
    didDrawPage: () => {
      addFooter();
    },
    styles: {
      fillColor: colors.cardBg,
      textColor: colors.text,
      fontSize: 9,
      cellPadding: 5,
    },
    headStyles: {
      fillColor: colors.primary,
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 9,
    },
    columnStyles: {
      0: { cellWidth: 30, fontStyle: "bold" },
      1: { cellWidth: 71 },
      2: { cellWidth: 30 },
      3: { cellWidth: 55 },
    },
    didParseCell: function (data) {
      if (data.section === "body" && data.column.index === 0) {
        const severity = data.cell.raw as string;
        switch (severity) {
          case "CRITICAL":
            data.cell.styles.textColor = colors.critical;
            break;
          case "HIGH":
            data.cell.styles.textColor = colors.high;
            break;
          case "MEDIUM":
            data.cell.styles.textColor = colors.medium;
            break;
          case "LOW":
            data.cell.styles.textColor = colors.low;
            break;
          default:
            data.cell.styles.textColor = colors.info;
        }
      }
    },
    didDrawCell: (data) => {
      if (data.section !== "body" || data.column.index !== 3) return;
      const finding = findings[data.row.index];
      const ref = toReferenceLink(finding?.reference);
      if (!ref.url) return;
      doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, { url: ref.url });
    },
    margin: { left: margin, right: margin },
  });

  if (enumeratedUsers.length > 0) {
    doc.addPage();
    paintStandardPage();

    const tableTitle = "Enumerated Users";
    autoTable(doc, {
      startY: 35,
      head: [["Username"]],
      body: enumeratedUsers.map((u) => [u]),
      theme: "plain",
      styles: { fontSize: 9, textColor: colors.text as [number, number, number] },
      headStyles: { fillColor: colors.primary as [number, number, number], textColor: [255, 255, 255] as [number, number, number], fontStyle: "bold" },
      bodyStyles: { fillColor: colors.cardBg as [number, number, number], textColor: colors.text as [number, number, number] },
      didParseCell: (data) => {
        if (data.section !== "body") return;
        const isAlt = data.row.index % 2 === 1;
        data.cell.styles.fillColor = (isAlt ? [30, 42, 60] : colors.cardBg) as [number, number, number];
        data.cell.styles.textColor = colors.text as [number, number, number];
      },
      margin: { left: margin, right: margin },
      willDrawPage: () => {
        paintStandardPage();
        doc.setFontSize(18);
        doc.setTextColor(...colors.text);
        doc.setFont("helvetica", "bold");
        doc.text(tableTitle, margin, 25);
      },
      didDrawPage: () => {
        addFooter();
      },
    });
    addFooter();
  }

  // === SCAN INFORMATION PAGE ===
  doc.addPage();
  doc.setFillColor(...colors.background);
  doc.rect(0, 0, pageWidth, pageHeight, "F");
  doc.setFillColor(...colors.primary);
  doc.rect(0, 0, pageWidth, 8, "F");

  yPosition = 25;

  doc.setFontSize(22);
  doc.setTextColor(...colors.text);
  doc.setFont("helvetica", "bold");
  doc.text("Scan Information", margin, yPosition);
  yPosition += 15;

  const scanTime = options?.scanTimestamp || customer.lastScan || "N/A";
  const scannersRow = scannersUsedText;
  const egressIpFinding = findings.find((f) => /^scan egress ip/i.test(String(f.title || "")));
  const egressIp = egressIpFinding ? String(egressIpFinding.description || "").match(/(\d{1,3}(?:\.\d{1,3}){3}|[a-f0-9:]{3,})/i)?.[1] : null;

  const scanInfo = [
    ["Target URL", customer.url],
    ["Contact", customer.contact],
    ...(egressIp ? [["Egress IP", egressIp]] : []),
    ["Last Scan", scanTime],
    ["Scanners Used", scannersRow],
    ["Report Generated", new Date().toLocaleString()],
  ];

  autoTable(doc, {
    startY: yPosition,
    body: scanInfo,
    theme: "plain",
    willDrawPage: () => {
      paintStandardPage();
    },
    didDrawPage: () => {
      addFooter();
    },
    styles: {
      fillColor: colors.cardBg,
      textColor: colors.text,
      fontSize: 10,
      cellPadding: 8,
    },
    columnStyles: {
      0: { fontStyle: "bold", textColor: colors.textMuted, cellWidth: 50 },
      1: { textColor: colors.text },
    },
    margin: { left: margin, right: margin },
  });

  const scanInfoFinalY = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY;
  yPosition = (scanInfoFinalY ?? yPosition) + 12;

  // Call to action
  checkPageBreak(45);
  doc.setFillColor(...colors.cardBg);
  doc.roundedRect(margin, yPosition, pageWidth - 2 * margin, 32, 3, 3, "F");
  doc.setFontSize(12);
  doc.setTextColor(...colors.primary);
  doc.setFont("helvetica", "bold");
  doc.text("Next Steps", margin + 8, yPosition + 12);
  doc.setFontSize(10);
  doc.setTextColor(...colors.text);
  doc.setFont("helvetica", "normal");
  const cta = "Prioritise remediation of critical/high findings and schedule a retest. Contact ShadowScan Security for help and remediation guidance.";
  const ctaLines = doc.splitTextToSize(cta, pageWidth - 2 * margin - 16);
  doc.text(ctaLines.slice(0, 2), margin + 8, yPosition + 22);

  addFooter();

  // Save the PDF
  const filename =
    options?.filename ||
    `ShadowScan-Report-${customer.name.replace(/\s+/g, "-")}-${new Date().toISOString().split("T")[0]}.pdf`;
  const output = options?.output || "download";
  if (output === "blob") {
    return doc.output("blob") as Blob;
  }
  doc.save(filename);
}
